import { type FormEvent, type JSX, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Icon } from '@/components/icons';
import { Badge, Button, Card, ConfirmModal, IconAction, Input, Skeleton, Spinner } from '@/components/ui';
import {
  openImpersonatedTab,
  useActivateCustomer,
  useImpersonate,
  useCreateCustomer,
  useCustomers,
  useSuspendCustomer,
  type Customer,
  type CreateCustomerInput,
} from '@/hooks/useAdminCustomers';
import { ApiError } from '@/lib/api';
import { formatCurrency, formatRelativeTime } from '@/lib/format';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Reseller admin → list + create + suspend/activate sub-tenants. Lives at
// /admin/customers; sidebar gates the link by tenant_type so non-resellers
// never see it. Backend also rejects with 403 either way.
// ---------------------------------------------------------------------------
export default function AdminCustomersPage(): JSX.Element {
  const { t, i18n } = useTranslation('default');
  const lang = (i18n.resolvedLanguage ?? i18n.language ?? 'en').slice(0, 2);

  const list = useCustomers();
  const items = list.data?.items ?? [];

  const [showNew, setShowNew] = useState(false);

  return (
    <div className="px-8 py-8 max-w-[1200px] mx-auto">
      <header className="flex items-end justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="display text-[28px] font-semibold tracking-tight text-text leading-none m-0">
            {t('ac_title')}
          </h1>
          <p className="text-[13px] text-text-3 mt-1.5">
            {items.length === 0
              ? t('ac_sub')
              : t('ac_count', { total: items.length.toLocaleString(lang) })}
          </p>
        </div>
        <Button onClick={() => setShowNew(true)}>
          <Icon.plus width="14" height="14" />
          {t('ac_new')}
        </Button>
      </header>

      <Card padding="none">
        {list.isLoading ? (
          <div className="p-5 space-y-2">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center">
            <p className="text-[14px] text-text-2 font-medium">{t('ac_empty_title')}</p>
            <p className="text-[13px] text-text-3 mt-1">{t('ac_empty_sub')}</p>
            <Button className="mt-5" onClick={() => setShowNew(true)}>
              <Icon.plus width="14" height="14" />
              {t('ac_new')}
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-[0.06em] text-text-4 bg-surface-2 border-b border-line">
                  <th className="px-5 py-2.5 font-medium">{t('ac_col_customer')}</th>
                  <th className="px-5 py-2.5 font-medium">{t('ac_col_status')}</th>
                  <th className="px-5 py-2.5 font-medium">{t('ac_col_country')}</th>
                  <th className="px-5 py-2.5 font-medium text-right">{t('ac_col_balance')}</th>
                  <th className="px-5 py-2.5 font-medium">{t('ac_col_joined')}</th>
                  <th className="px-5 py-2.5 font-medium text-right">{t('ac_col_actions')}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((c) => (
                  <CustomerRow key={c.id} c={c} lang={lang} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <NewCustomerModal open={showNew} onClose={() => setShowNew(false)} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Row
// ---------------------------------------------------------------------------
function CustomerRow({ c, lang }: { c: Customer; lang: string }): JSX.Element {
  const { t } = useTranslation('default');
  const navigate = useNavigate();
  const suspend = useSuspendCustomer();
  const activate = useActivateCustomer();
  const impersonate = useImpersonate();
  const [confirm, setConfirm] = useState<'suspend' | 'activate' | null>(null);

  const busy = suspend.isPending || activate.isPending;

  // Whole-row click → detail. Action buttons inside the actions cell
  // call e.stopPropagation so they don't double-fire.
  const goToDetail = () => navigate(`/admin/customers/${c.id}`);

  const handleImpersonate = async () => {
    try {
      const res = await impersonate.mutateAsync(c.id);
      openImpersonatedTab(res.access_token, res.customer_id, res.customer_name);
    } catch {
      // Surfaced by the global error toast; for MVP a no-op is fine.
    }
  };

  const onConfirm = async () => {
    try {
      if (confirm === 'suspend') await suspend.mutateAsync(c.id);
      else if (confirm === 'activate') await activate.mutateAsync(c.id);
    } catch {
      // Swallow — failures will surface on the next list refresh; the
      // confirm modal closes either way to avoid trapping the operator.
    } finally {
      setConfirm(null);
    }
  };

  // Initials avatar — first letter of name. Same volt-gradient look as
  // the Settings team page so the carrier admin sees a familiar pattern.
  const initials = c.name.trim().charAt(0).toUpperCase() || '?';

  return (
    <>
      <tr
        className="border-b border-line last:border-0 hover:bg-surface-2 cursor-pointer"
        onClick={goToDetail}
      >
        <td className="px-5 py-3">
          <div className="flex items-center gap-3">
            <span
              aria-hidden="true"
              className="w-8 h-8 rounded-full grid place-items-center text-white text-[12px] font-bold flex-shrink-0"
              style={{ background: 'linear-gradient(135deg, var(--volt-500), var(--volt-700))' }}
            >
              {initials}
            </span>
            <div className="min-w-0">
              <Link
                to={`/admin/customers/${c.id}`}
                className="text-text font-medium truncate hover:text-volt-700 hover:underline underline-offset-2"
              >
                {c.name}
              </Link>
              {c.owner_email && (
                <div className="text-[12px] text-text-3 truncate">{c.owner_email}</div>
              )}
            </div>
          </div>
        </td>
        <td className="px-5 py-3">
          <Badge variant={c.status === 'active' ? 'success' : c.status === 'suspended' ? 'warn' : 'neutral'}>
            {t(`ac_status_${c.status}`)}
          </Badge>
        </td>
        <td className="px-5 py-3 text-text-2 mono text-[12px]">{c.country || '—'}</td>
        <td className="px-5 py-3 num text-right text-text font-medium whitespace-nowrap">
          {formatCurrency(c.balance, lang)}
        </td>
        <td className="px-5 py-3 text-text-3 text-[12px] whitespace-nowrap">
          <span title={new Date(c.created_at).toLocaleString(lang)}>
            {formatRelativeTime(c.created_at, lang)}
          </span>
        </td>
        <td className="px-5 py-3 text-right" onClick={(e) => e.stopPropagation()}>
          <div className="inline-flex gap-0.5">
            {/* eye = open customer detail page (read-only inspection) */}
            <IconAction
              icon={Icon.eye}
              label={t('ac_action_view')}
              onClick={goToDetail}
            />
            {/* loginAs = sign in AS this customer (impersonate). Distinct
                icon from the plain view eye so the operator doesn't
                accidentally land in someone else's session. */}
            {c.status === 'active' && (
              <IconAction
                icon={Icon.loginAs}
                label={t('ac_action_view_as')}
                onClick={() => void handleImpersonate()}
                disabled={impersonate.isPending || busy}
              />
            )}
            {c.status === 'active' ? (
              <IconAction
                icon={Icon.stop}
                label={t('ac_action_suspend')}
                onClick={() => setConfirm('suspend')}
                disabled={busy}
                tone="warn"
              />
            ) : c.status === 'suspended' ? (
              <IconAction
                icon={Icon.play}
                label={t('ac_action_activate')}
                onClick={() => setConfirm('activate')}
                disabled={busy}
              />
            ) : null}
          </div>
        </td>
      </tr>
      <ConfirmModal
        open={confirm !== null}
        title={
          confirm === 'suspend'
            ? t('ac_suspend_title')
            : t('ac_activate_title')
        }
        description={
          confirm === 'suspend'
            ? t('ac_suspend_desc', { name: c.name })
            : t('ac_activate_desc', { name: c.name })
        }
        confirmLabel={
          confirm === 'suspend' ? t('ac_action_suspend') : t('ac_action_activate')
        }
        cancelLabel={t('ac_cancel')}
        tone={confirm === 'suspend' ? 'danger' : 'primary'}
        busy={busy}
        onConfirm={() => void onConfirm()}
        onClose={() => setConfirm(null)}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// New customer modal
// ---------------------------------------------------------------------------
interface NewCustomerModalProps {
  open: boolean;
  onClose: () => void;
}

function NewCustomerModal({ open, onClose }: NewCustomerModalProps): JSX.Element | null {
  const { t } = useTranslation('default');
  const create = useCreateCustomer();
  const [form, setForm] = useState<CreateCustomerInput>({
    name: '',
    tenant_type: 'customer',
    owner_email: '',
    owner_password: '',
    owner_full_name: '',
    country: 'US',
    timezone: 'America/New_York',
    initial_credit: 0,
    send_invite: true,
  });
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await create.mutateAsync(form);
      onClose();
      setForm({
        name: '',
        tenant_type: 'customer',
        owner_email: '',
        owner_password: '',
        owner_full_name: '',
        country: 'US',
        timezone: 'America/New_York',
        initial_credit: 0,
        send_invite: true,
      });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('ac_err_generic'));
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-ink/50 px-4"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="bg-surface border border-line rounded-lg shadow-lg w-full max-w-[520px] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-line">
          <h2 className="display text-[16px] font-semibold text-text">{t('ac_new_title')}</h2>
          <p className="text-[12px] text-text-3 mt-0.5">{t('ac_new_sub')}</p>
        </div>
        <form onSubmit={(e) => void submit(e)} className="px-5 py-4 space-y-4">
          {error && (
            <div className="px-3 py-2 bg-danger-bg border border-danger-bd rounded-md text-[13px] text-danger">
              {error}
            </div>
          )}

          <Field label={t('ac_field_name')} hint={t('ac_field_name_hint')}>
            <Input
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              placeholder="Acme Corp"
              required
            />
          </Field>

          <Field label={t('ac_field_type')} hint={t('ac_field_type_hint')}>
            <select
              value={form.tenant_type ?? 'customer'}
              onChange={(e) =>
                setForm((p) => ({
                  ...p,
                  tenant_type: e.target.value as 'customer' | 'reseller',
                }))
              }
              className="w-full h-9 px-3 rounded-md border border-line-strong bg-surface text-[13px] text-text focus:border-volt-600 focus:outline-none focus:ring-2 focus:ring-volt-glow"
            >
              <option value="customer">{t('ac_field_type_customer')}</option>
              <option value="reseller">{t('ac_field_type_reseller')}</option>
            </select>
          </Field>

          <Field label={t('ac_field_owner_email')}>
            <Input
              type="email"
              value={form.owner_email}
              onChange={(e) => setForm((p) => ({ ...p, owner_email: e.target.value }))}
              placeholder="admin@acme.com"
              required
            />
          </Field>

          {/* Invite vs manual-password toggle. Defaults to invite so the
              admin doesn't have to invent (and out-of-band share) a
              throwaway password — the owner sets their own via the
              magic-link. Manual password mode stays available for
              quick local testing / restores. */}
          <div className="border border-line rounded-md divide-y divide-line">
            <label className="flex items-start gap-3 p-3 cursor-pointer">
              <input
                type="checkbox"
                checked={form.send_invite ?? true}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    send_invite: e.target.checked,
                    owner_password: e.target.checked ? '' : p.owner_password,
                  }))
                }
                className="mt-1"
              />
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-medium text-text">
                  {t('ac_field_send_invite', 'Send magic-link invite email')}
                </div>
                <div className="text-[11.5px] text-text-3 mt-0.5">
                  {t(
                    'ac_field_send_invite_hint',
                    "Owner gets an email with a link to set their own password. Skip if you're sharing a temp password out-of-band.",
                  )}
                </div>
                {!form.send_invite && (
                  <div className="mt-3">
                    <label className="block text-[12px] font-medium text-text-2 mb-1.5">
                      {t('ac_field_owner_password')}
                    </label>
                    <Input
                      type="password"
                      value={form.owner_password}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, owner_password: e.target.value }))
                      }
                      placeholder="min 8 chars"
                      minLength={8}
                      required={!form.send_invite}
                    />
                  </div>
                )}
              </div>
            </label>
          </div>

          <Field label={t('ac_field_owner_full_name')}>
            <Input
              value={form.owner_full_name}
              onChange={(e) => setForm((p) => ({ ...p, owner_full_name: e.target.value }))}
              placeholder="Jane Doe"
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label={t('ac_field_country')} hint={t('ac_field_country_hint')}>
              <Input
                value={form.country}
                onChange={(e) =>
                  setForm((p) => ({ ...p, country: e.target.value.toUpperCase().slice(0, 2) }))
                }
                maxLength={2}
              />
            </Field>
            <Field label={t('ac_field_initial_credit')} hint={t('ac_field_initial_credit_hint')}>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={form.initial_credit ?? 0}
                onChange={(e) =>
                  setForm((p) => ({ ...p, initial_credit: Number(e.target.value) || 0 }))
                }
              />
            </Field>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="secondary" onClick={onClose} disabled={create.isPending}>
              {t('ac_cancel')}
            </Button>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? (
                <>
                  <Spinner />
                  <span>{t('ac_creating')}</span>
                </>
              ) : (
                t('ac_create')
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: JSX.Element;
}): JSX.Element {
  return (
    <div>
      <label className={cn('block text-[12px] font-medium text-text-2 mb-1.5')}>{label}</label>
      {children}
      {hint && <p className="text-[12px] text-text-4 mt-1">{hint}</p>}
    </div>
  );
}
